/**
 * API client for Centre Operations -> Team Wall (teacher & team posters).
 *
 * Backend: oh_operations Django app, alfred-suffixed endpoints under
 * /api/v1/oh_operations/team_wall/. Drafts store the whole builder state
 * (member photos as data URLs) in `data`; the list endpoint omits `data` on
 * purpose. Poster PDFs are rendered client-side and never stored.
 */
import { sentryLogger } from 'sentry'

import { apiEndpoints } from './common'

import { makeRequestUrl } from '../app/helpers'
import { get, patch, post, remove } from '../request'

// Draft picker rows for a centre — uuid/name/modified only (no data payload).
const fetchTeamWallDrafts = async (centreUuid) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.teamWallDrafts}/alfred`, {
        centre: centreUuid
      })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching team wall drafts')
    throw error
  }
}

// Full draft including `data`, to hydrate the builder.
const fetchTeamWallDraft = async (uuid) => {
  try {
    const response = await get(`${apiEndpoints.teamWallDrafts}/${uuid}/alfred`)
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching team wall draft')
    throw error
  }
}

// Save a draft. Upserts by (centre, name) server-side, so re-saving
// "July 2026" replaces that draft instead of stacking copies.
const saveTeamWallDraft = async ({ centre, name, data }) => {
  try {
    const response = await post(`${apiEndpoints.teamWallDrafts}/alfred`, {
      centre,
      name,
      data
    })
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in saving team wall draft')
    throw error
  }
}

// Rename and/or overwrite an existing draft.
const updateTeamWallDraft = async (uuid, { name, data }) => {
  try {
    const response = await patch(
      `${apiEndpoints.teamWallDrafts}/${uuid}/alfred`,
      { name, data }
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in updating team wall draft')
    throw error
  }
}

const deleteTeamWallDraft = async (uuid) => {
  try {
    await remove(`${apiEndpoints.teamWallDrafts}/${uuid}/alfred`)
    return uuid
  } catch (error) {
    sentryLogger(error, 'error in deleting team wall draft')
    throw error
  }
}

// Read a centre's duplicated "our team members" roster sheet server-side and
// return member rows ({section, name, role}) to merge into the builder.
const importTeamWallMembers = async ({ sheet, tab }) => {
  try {
    const response = await post(`${apiEndpoints.teamWallImport}/alfred`, {
      sheet,
      tab
    })
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in importing team wall members')
    throw error
  }
}

// Read the org-wide "Teacher ID Cards" form responses sheet server-side and
// return this centre's people (contact, emergency contact, curated card
// title, bio, inlined photo) to merge into the roster.
const importTeamWallIdForm = async (centreUuid) => {
  try {
    const response = await post(`${apiEndpoints.teamWallIdFormImport}/alfred`, {
      centre: centreUuid
    })
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in importing id-card form members')
    throw error
  }
}

// Queue the background sweep that fills EVERY centre's roster from the
// Teacher ID Cards form (text + face-cropped photos; takes a few minutes).
const importTeamWallIdFormAll = async () => {
  try {
    const response = await post(`${apiEndpoints.teamWallIdFormImport}/alfred`, {
      all: true
    })
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in queueing the all-centre id-form import')
    throw error
  }
}

// One form photo, face-cropped server-side (Cloudinary) with a face count.
// Called per member after the text-only ID-form import.
const importTeamWallIdPhoto = async (fileId) => {
  try {
    const response = await post(`${apiEndpoints.teamWallIdPhoto}/alfred`, {
      file_id: fileId
    })
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in processing id-card photo')
    throw error
  }
}

// HQ: which centre has made a newsletter / event creative (zeros included).
const fetchNewsletterHqSummary = async (commType) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.newsletterHqSummary}/alfred`, {
        comm_type: commType
      })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching newsletter hq summary')
    throw error
  }
}

// HQ: every centre's roster members in one filterable list (photos stripped).
const fetchTeamWallHqMembers = async () => {
  try {
    const response = await get(`${apiEndpoints.teamWallHqMembers}/alfred`)
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching hq roster members')
    throw error
  }
}

export {
  fetchTeamWallDrafts,
  fetchTeamWallDraft,
  saveTeamWallDraft,
  updateTeamWallDraft,
  deleteTeamWallDraft,
  importTeamWallMembers,
  importTeamWallIdForm,
  importTeamWallIdFormAll,
  importTeamWallIdPhoto,
  fetchNewsletterHqSummary,
  fetchTeamWallHqMembers
}
